import { useState, useEffect } from "react";
import { Menu, X, ChevronDown, ChevronUp } from "lucide-react";

type NavSubItem = {
  label: string;
  href: string;
};

type NavItem = {
  label: string;
  href: string;
  children?: NavSubItem[];
};

const navItems: NavItem[] = [
  { label: "Home", href: "/" },
  {
    label: "About Us",
    href: "#",
    children: [
      { label: "Company Profile", href: "/company-profile" },
      { label: "Vision & Mission", href: "/mission-vision" },
      { label: "Chairman & CEO Message", href: "/chairman-ceo" },
      { label: "Board of Directors", href: "/board-of-directors" },
      { label: "Accreditation", href: "/accreditation" },
      { label: "Recognition", href: "/recognition" },
    ],
  },
  {
    label: "Our Expertise",
    href: "#",
    children: [
      { label: "Manpower Solutions", href: "/manpower-supply" },
      { label: "Security Services", href: "/security-services" },
      { label: "Executive Search & Recruitment", href: "/recruitment" },
      { label: "HR Consultancy & Learning", href: "/hr-consultancy" },
      { label: "Business Process Solutions", href: "/business-process-solutions" },
    ],
  },
  {
    label: "Sectors",
    href: "#",
    children: [
      { label: "Construction", href: "/construction" },
      { label: "E-Commerce & Delivery", href: "/ecommerce-delivery" },
      { label: "Hospitality & Retail", href: "/hospitality" },
      { label: "Aviation", href: "/aviation-and-government" },
      { label: "Banking & Finance", href: "/banking-finance" },
      { label: "Events & Entertainment", href: "/entertainment-events" },
      { label: "Healthcare", href: "/health-care" },
      { label: "Technology Solutions", href: "/it" },
      { label: "Warehouse & Logistics", href: "/logistics" },
    ],
  },
  {
    label: "News",
    href: "#",
    children: [
      { label: "Press Release", href: "/press-release" },
      { label: "Blogs", href: "/blog" },
    ],
  },
  { label: "Careers", href: "/jobs" },
  { label: "Contact Us", href: "/contact" },
];

const MobileNavItem = ({ item, closeMenu }: { item: NavItem; closeMenu: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!item.children) {
    return (
      <li className="border-b border-border">
        <a href={item.href} onClick={closeMenu} className="block py-4 text-sm font-medium text-brand-navy hover:bg-gray-100 px-5">
          {item.label}
        </a>
      </li>
    );
  }

  return (
    <li className="border-b border-border">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center w-full py-4 text-sm font-medium text-brand-navy hover:bg-gray-100 px-5 text-left"
      >
        <span>{item.label}</span>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && (
        <ul className="pl-6 bg-white">
          {item.children.map((child) => (
            <li key={child.label}>
              <a href={child.href} onClick={closeMenu} className="block py-3 text-sm text-medium-gray hover:text-brand-navy">
                {child.label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const HeaderNavigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const navTextColor = isScrolled ? "text-primary" : "text-white";

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg h-24' : 'bg-transparent h-28'}`}>
        <div className="max-w-[1320px] mx-auto pl-0 pr-4 sm:px-6 lg:px-[15px] h-full">
          <div className="flex items-center justify-between w-full h-full">
            <div className="flex-shrink-0">
              <a href="/">
                <img
                  src="/logo.png"
                  alt="I.H.C.L."
                  className="w-[102px] sm:w-[240px] h-14 sm:h-16 object-contain"
                />
              </a>
            </div>

            <nav className="hidden lg:flex">
              <ul className="flex items-center space-x-9">
                {navItems.map((item) => (
                  <li key={item.label} className="relative group">
                    <a href={item.href} className={`flex items-center space-x-1 ${navTextColor} hover:text-brand-gold transition-colors text-[15px] font-medium tracking-wide`}>
                      <span>{item.label.toUpperCase()}</span>
                      {item.children && <ChevronDown className="h-4 w-4" />}
                    </a>
                    {item.children && (
                      <ul className="absolute top-full left-1/2 -translate-x-1/2 mt-5 w-64 origin-top rounded-sm bg-white/95 backdrop-blur-sm shadow-2xl ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-40">
                        <div className="py-2">
                          {item.children.map((child) => (
                            <li key={child.label}>
                              <a href={child.href} className="text-gray-700 block px-5 py-2.5 text-sm hover:bg-gray-100 hover:text-brand-navy font-medium">
                                {child.label}
                              </a>
                            </li>
                          ))}
                        </div>
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </nav>

            <div className="flex items-center lg:hidden">
              <button onClick={() => setIsMobileMenuOpen(true)} className={`p-2 rounded-md ${navTextColor}`}>
                <Menu className="h-7 w-7" />
                <span className="sr-only">Open main menu</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className={`fixed inset-0 z-[100] transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
        
        <div className={`fixed top-0 right-0 h-full w-[320px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex justify-between items-center py-4 px-5 border-b border-border">
            <img
              src="/logo.png"
              alt="I.H.C.L."
              className="w-[180px] h-auto"
            />
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 -mr-2">
              <X className="h-6 w-6 text-gray-700" />
              <span className="sr-only">Close menu</span>
            </button>
          </div>
          <nav className="overflow-y-auto h-[calc(100%-73px)]">
            <ul className="flex flex-col">
              {navItems.map((item) => <MobileNavItem key={item.label} item={item} closeMenu={() => setIsMobileMenuOpen(false)} />)}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default HeaderNavigation;